import React, { useCallback, useMemo, useState } from "react";
import axios from "axios";
import { Redirect, useHistory } from "react-router-dom";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import SelectClass from "../ChildComponent/SelectClass";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";

function formatAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-IN");
}

function getPayValue(student, metricKey) {
  return Number(student?.pay_details?.[0]?.[metricKey] || 0);
}

function getClassLabel(student) {
  const className = student.class_name || "—";
  const sectionId = student.section_id;
  const sectionName = student.section_name;

  if (!sectionId || sectionId === "No" || !sectionName) {
    return className;
  }
  return `${className} · ${sectionName}`;
}

function getStudentInitial(name) {
  const trimmed = String(name || "").trim();
  return (trimmed[0] || "?").toUpperCase();
}

function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = Boolean(imagePath) && !imgFailed;

  return (
    <div className="csfs-avatar-wrap">
      {showImage ? (
        <img
          src={imagePath}
          alt={name || "Student"}
          className="csfs-avatar"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="csfs-avatar-initial" aria-hidden="true">
          {getStudentInitial(name)}
        </span>
      )}
    </div>
  );
}

const ClassStudentFeeSummary = ({
  title,
  metricKey,
  metricLabel,
  accentColor = COLORS.primary_color_shade6 || "#039BE5",
  showOnlyPositive = true,
  showCurrentDueChip = false,
}) => {
  const history = useHistory();
  const [loggedIn] = useState(!!localStorage.getItem("token"));
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);

  const fetchStudentList = useCallback(async (class_id, section_id, session_id) => {
    setLoading(true);
    setStudents([]);

    try {
      const schoolCode = SessionManager.getSchoolCode();
      const url =
        process.env.REACT_APP_SERVER_NAME +
        process.env.REACT_APP_TARGET_DIR +
        "getClassStudentPaylistNew.php";

      const response = await axios.get(url, {
        params: {
          school_code: schoolCode,
          class_id,
          section_id,
          session_id,
        },
      });

      if (response.data.api_status === "OK") {
        setStudents(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        myToast.notify_warning(
          response.data.api_remark || "No student payment records found."
        );
      }
    } catch (error) {
      console.error(`${title} fetch error:`, error);
      myToast.notify_error("An error occurred while fetching student details.");
    } finally {
      setLoading(false);
    }
  }, [title]);

  const handleClassSelect = useCallback(
    (class_id, section_id, session_id) => {
      fetchStudentList(class_id, section_id, session_id);
    },
    [fetchStudentList]
  );

  const visibleStudents = useMemo(() => {
    if (!showOnlyPositive) return students;
    return students.filter((student) => getPayValue(student, metricKey) > 0);
  }, [students, metricKey, showOnlyPositive]);

  const totalAmount = useMemo(
    () =>
      visibleStudents.reduce(
        (sum, student) => sum + getPayValue(student, metricKey),
        0
      ),
    [visibleStudents, metricKey]
  );

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div className="content" style={{ background: COLORS.white }}>
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <a href="#">{title}</a>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton onClick={() => history.goBack()} />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow csfs-page"
        style={{ "--csfs-accent": accentColor, "--mMargin": "50px" }}
      >
        <SelectClass action={handleClassSelect} selected_position="0" />

        {loading ? (
          <div className="csfs-loader-wrap">
            <MyLoader />
          </div>
        ) : visibleStudents.length === 0 ? (
          <div className="csfs-empty">
            <div className="csfs-empty-icon">
              <i className="fa fa-users" aria-hidden="true" />
            </div>
            <div className="csfs-empty-title">No records found</div>
            <div className="csfs-empty-text">
              Select a class to view {metricLabel.toLowerCase()} details.
            </div>
          </div>
        ) : (
          <>
            <div className="csfs-summary-banner">
              <div className="csfs-summary-left">
                <span className="csfs-summary-icon">
                  <i className="fa fa-calculator" aria-hidden="true" />
                </span>
                <div>
                  <div className="csfs-summary-label">Class Total {metricLabel}</div>
                  <div className="csfs-summary-amount">₹{formatAmount(totalAmount)}</div>
                </div>
              </div>
              <div className="csfs-summary-count">
                {visibleStudents.length} student{visibleStudents.length !== 1 ? "s" : ""}
              </div>
            </div>

            <div className="csfs-list">
              {visibleStudents.map((item, index) => {
                const amount = getPayValue(item, metricKey);
                const currentDue = Number(item.pay_details?.[0]?.current_due || 0);

                return (
                  <div
                    key={`${item.student_enrollment_no || "student"}-${index}`}
                    className="csfs-card"
                  >
                    <div className="csfs-card-main">
                      <StudentAvatar
                        name={item.stu_name}
                        imagePath={item.student_img_path}
                      />

                      <div className="csfs-info">
                        <div className="csfs-name-row">
                          <span className="csfs-name">{item.stu_name || "—"}</span>
                        </div>
                        {item.father_name && (
                          <div className="csfs-father">
                            <i className="fa fa-user" aria-hidden="true" />
                            {item.father_name}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="csfs-meta csfs-meta-center">
                      <span className="csfs-meta-chip csfs-meta-chip-class">
                        <span className="csfs-chip-icon">
                          <i className="fa fa-graduation-cap" aria-hidden="true" />
                        </span>
                        <span className="csfs-chip-text">{getClassLabel(item)}</span>
                      </span>
                      <span className="csfs-meta-chip csfs-meta-chip-roll">
                        <span className="csfs-chip-icon">
                          <i className="fa fa-hashtag" aria-hidden="true" />
                        </span>
                        <span className="csfs-chip-text">
                          Roll {item.student_roll_no ?? "—"}
                        </span>
                      </span>
                      {item.student_enrollment_no && (
                        <span className="csfs-meta-chip csfs-meta-chip-enroll">
                          <span className="csfs-chip-icon">
                            <i className="fa fa-id-card" aria-hidden="true" />
                          </span>
                          <span className="csfs-chip-text">
                            {item.student_enrollment_no}
                          </span>
                        </span>
                      )}
                    </div>

                    <div className="csfs-amounts-col">
                      {showCurrentDueChip && (
                        <div className="csfs-amount-block csfs-amount-block-current">
                          <span className="csfs-amount-icon" aria-hidden="true">
                            <i className="fa fa-clock-o" />
                          </span>
                          <div className="csfs-amount-body">
                            <div className="csfs-amount-label">Current Due</div>
                            <div className="csfs-amount-value">
                              ₹{formatAmount(currentDue)}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="csfs-amount-block">
                        <span className="csfs-amount-icon" aria-hidden="true">
                          <i className="fa fa-inr" />
                        </span>
                        <div className="csfs-amount-body">
                          <div className="csfs-amount-label">{metricLabel}</div>
                          <div className="csfs-amount-value">₹{formatAmount(amount)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <style>{`
        .csfs-page {
          padding-bottom: 20px;
          background: #ffffff !important;
        }
        .csfs-loader-wrap {
          display: flex;
          justify-content: center;
          padding: 40px 0;
        }
        .csfs-empty {
          text-align: center;
          padding: 48px 20px;
          color: #6b7280;
        }
        .csfs-empty-icon {
          width: 56px;
          height: 56px;
          margin: 0 auto 14px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #9ca3af;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
        }
        .csfs-empty-title {
          font-size: 16px;
          font-weight: 700;
          color: #374151;
          margin-bottom: 6px;
        }
        .csfs-empty-text {
          font-size: 13px;
        }
        .csfs-summary-banner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin: 16px 0 14px;
          padding: 14px 16px;
          border-radius: 14px;
          background: linear-gradient(135deg, var(--csfs-accent) 0%, ${COLORS.primary_color_shade7 || "#0288D1"} 100%);
          color: #fff;
          border: none;
          box-shadow: 0 4px 16px color-mix(in srgb, var(--csfs-accent) 35%, transparent);
        }
        .csfs-summary-left {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }
        .csfs-summary-icon {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .csfs-summary-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: rgba(255, 255, 255, 0.92);
        }
        .csfs-summary-amount {
          font-size: 24px;
          font-weight: 800;
          line-height: 1.2;
          margin-top: 2px;
          color: #fff;
        }
        .csfs-summary-count {
          font-size: 12px;
          font-weight: 700;
          padding: 6px 12px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.18);
          border: 1px solid rgba(255, 255, 255, 0.28);
          color: #fff;
          white-space: nowrap;
        }
        .csfs-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          background: #ffffff;
        }
        .csfs-card {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
          align-items: center;
          gap: 12px 16px;
          padding: 12px 14px;
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid ${COLORS.grey3 || "#E0E0E0"};
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
        }
        .csfs-card:hover {
          transform: translateY(-1px);
          border-color: color-mix(in srgb, var(--csfs-accent) 40%, #e5e7eb);
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
        }
        .csfs-card-main {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
          justify-self: start;
        }
        .csfs-avatar-wrap {
          width: 60px;
          height: 60px;
          border-radius: 16px;
          overflow: hidden;
          flex-shrink: 0;
          border: 2px solid color-mix(in srgb, var(--csfs-accent) 25%, #e5e7eb);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          background: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .csfs-avatar {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .csfs-avatar-initial {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 800;
          color: var(--csfs-accent);
          background: color-mix(in srgb, var(--csfs-accent) 12%, #ffffff);
          user-select: none;
        }
        .csfs-info {
          min-width: 0;
          flex: 1;
        }
        .csfs-name-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 4px;
        }
        .csfs-name {
          font-size: 15px;
          font-weight: 800;
          color: ${COLORS.black8 || "#333"};
        }
        .csfs-father {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #6b7280;
          margin-bottom: 6px;
        }
        .csfs-father .fa {
          font-size: 11px;
          color: #9ca3af;
        }
        .csfs-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .csfs-meta-center {
          justify-content: center;
          justify-self: center;
          align-self: center;
          max-width: 100%;
        }
        .csfs-meta-chip {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          max-width: 100%;
          height: 32px;
          box-sizing: border-box;
          padding: 0 10px 0 5px;
          border-radius: 999px;
          border: 1px solid transparent;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .csfs-meta-chip:hover {
          transform: translateY(-1px);
          box-shadow: 0 3px 8px rgba(15, 23, 42, 0.08);
        }
        .csfs-chip-icon {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 10px;
        }
        .csfs-chip-text {
          font-size: 11px;
          font-weight: 700;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .csfs-meta-chip-class {
          background: color-mix(in srgb, ${COLORS.primary_color_shade8 || "#0277BD"} 9%, #ffffff);
          border-color: color-mix(in srgb, ${COLORS.primary_color_shade8 || "#0277BD"} 22%, #e5e7eb);
          color: ${COLORS.primary_color_shade8 || "#0277BD"};
        }
        .csfs-meta-chip-class .csfs-chip-icon {
          background: color-mix(in srgb, ${COLORS.primary_color_shade8 || "#0277BD"} 16%, #ffffff);
          color: ${COLORS.primary_color_shade8 || "#0277BD"};
          border: 1px solid color-mix(in srgb, ${COLORS.primary_color_shade8 || "#0277BD"} 20%, #e5e7eb);
        }
        .csfs-meta-chip-roll {
          background: color-mix(in srgb, var(--csfs-accent) 10%, #ffffff);
          border-color: color-mix(in srgb, var(--csfs-accent) 26%, #e5e7eb);
          color: var(--csfs-accent);
        }
        .csfs-meta-chip-roll .csfs-chip-icon {
          background: color-mix(in srgb, var(--csfs-accent) 18%, #ffffff);
          color: var(--csfs-accent);
          border: 1px solid color-mix(in srgb, var(--csfs-accent) 24%, #e5e7eb);
        }
        .csfs-meta-chip-roll .csfs-chip-text {
          font-size: 14px;
          font-weight: 800;
          line-height: 1;
          letter-spacing: 0.01em;
        }
        .csfs-meta-chip-enroll {
          background: #f8fafc;
          border-color: #e2e8f0;
          color: #64748b;
        }
        .csfs-meta-chip-enroll .csfs-chip-icon {
          background: #ffffff;
          color: #94a3b8;
          border: 1px solid #e2e8f0;
        }
        .csfs-amounts-col {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 8px;
          justify-self: end;
          align-items: center;
          justify-content: flex-end;
        }
        .csfs-amount-block {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
          min-width: 132px;
          padding: 8px 12px 8px 8px;
          border-radius: 999px;
          background: color-mix(in srgb, var(--csfs-accent) 10%, #ffffff);
          border: 1px solid color-mix(in srgb, var(--csfs-accent) 24%, #e5e7eb);
          box-shadow: 0 2px 8px color-mix(in srgb, var(--csfs-accent) 12%, rgba(15, 23, 42, 0.06));
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .csfs-card:hover .csfs-amount-block {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px color-mix(in srgb, var(--csfs-accent) 18%, rgba(15, 23, 42, 0.1));
        }
        .csfs-amount-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 14px;
          font-weight: 700;
          color: #fff;
          background: linear-gradient(135deg, var(--csfs-accent) 0%, ${COLORS.primary_color_shade7 || "#0288D1"} 100%);
          box-shadow: 0 3px 8px color-mix(in srgb, var(--csfs-accent) 30%, transparent);
        }
        .csfs-amount-body {
          text-align: right;
          min-width: 0;
        }
        .csfs-amount-label {
          font-size: 9px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.07em;
          color: #94a3b8;
          line-height: 1.2;
        }
        .csfs-amount-value {
          font-size: 17px;
          font-weight: 800;
          color: var(--csfs-accent);
          line-height: 1.15;
          margin-top: 1px;
          letter-spacing: -0.02em;
        }
        .csfs-amount-block-current {
          background: color-mix(in srgb, #f59e0b 10%, #ffffff);
          border-color: color-mix(in srgb, #f59e0b 26%, #e5e7eb);
          box-shadow: 0 2px 8px color-mix(in srgb, #f59e0b 12%, rgba(15, 23, 42, 0.06));
        }
        .csfs-amount-block-current .csfs-amount-icon {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          box-shadow: 0 3px 8px color-mix(in srgb, #f59e0b 30%, transparent);
        }
        .csfs-amount-block-current .csfs-amount-value {
          color: #d97706;
        }
        .csfs-card:hover .csfs-amount-block-current {
          box-shadow: 0 4px 12px color-mix(in srgb, #f59e0b 18%, rgba(15, 23, 42, 0.1));
        }
        @media (max-width: 768px) {
          .csfs-card {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          .csfs-card-main {
            width: 100%;
            justify-self: stretch;
          }
          .csfs-meta-center {
            width: 100%;
            justify-self: stretch;
            justify-content: center;
          }
          .csfs-amounts-col {
            width: 100%;
            justify-self: stretch;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: stretch;
          }
          .csfs-amount-block {
            flex: 1;
            min-width: 140px;
            justify-content: space-between;
            justify-self: stretch;
            border-radius: 14px;
            padding: 10px 12px;
          }
          .csfs-amount-body {
            text-align: right;
            flex: 1;
          }
          .csfs-summary-banner {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>
    </div>
  );
};

export default ClassStudentFeeSummary;
